import { computeMidString } from './sortkey.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { appendMissingParentInputs } from './parentInputs.js'
import { resolveDomainEventOrDecision, resolveSchema } from './workflowSpec.js'
import { getEventColorByName, EventColorNames } from '../constants/eventColors.js'
import { computeStableOffsets, reconcileEventGroupIds, recalculateGroupSlots } from './diagramLayout.js'

const generateId = () => globalThis.crypto.randomUUID()

const findCardTypeByRole = (cardTypes, role) => cardTypes.find((ct) => ct.domainModelRole === role)

export const buildCreateEventMutation = ({
  workflow,
  workflowId,
  args,
  cardTypes = [],
  lastCardSortkey = '',
  batchEventsByDescription = null,
  settleOffsets = false
}) => {
  const { follows, lane, group, color, aggregateRoot, acceptanceCriteria, conditionLabel } = args
  let { type } = args

  const description = typeof args.description === 'string' ? args.description.trim() : ''
  if (!description) {
    return { error: 'Event description is required and cannot be blank.' }
  }

  const domainEvents = workflow.domainEvents || []
  const lanes = workflow.lanes || []
  const isStartPoint = follows === 'start'

  let resolvedParentId = follows
  let parentEvent = null
  let parentIndex = -1
  let existingChildren = []

  if (!isStartPoint) {
    let resolvedParent = resolveDomainEventOrDecision(follows, workflow)
    if (!resolvedParent && batchEventsByDescription) {
      resolvedParent = batchEventsByDescription.get(follows) || null
    }
    if (!resolvedParent) {
      return {
        error: `Parent event "${follows}" not found. Use a $ref path (from get_workflow), "start" to create a new starting point, or the description of an event created earlier in this same batch.`
      }
    }
    resolvedParentId = resolvedParent.id

    parentEvent = domainEvents.find((e) => e.id === resolvedParentId)
    parentIndex = domainEvents.findIndex((e) => e.id === resolvedParentId)
    existingChildren = domainEvents.filter((e) => e.parents && e.parents.includes(resolvedParentId))
  }

  const isParallel = args.parallel === true && parentEvent?.type === 'bpmn:Task'

  const trimmedLane = typeof lane === 'string' ? lane.trim() : ''
  if (!trimmedLane) {
    return { error: 'Lane name is required and cannot be blank.' }
  }

  let matchedLane = lanes.find((l) => l.name === trimmedLane)
  let laneAutoCreated = false
  let updatedLanes = null
  if (!matchedLane) {
    matchedLane = { id: generateId(), name: trimmedLane }
    updatedLanes = [...lanes, matchedLane]
    laneAutoCreated = true
  }
  const laneId = matchedLane.id
  const laneIndex = (updatedLanes || lanes).findIndex((l) => l.id === laneId)

  const TYPE_ALIASES = {
    domainEvent: 'bpmn:Task',
    decision: 'bpmn:ExclusiveGateway'
  }
  const internalType = TYPE_ALIASES[type]
  if (!internalType) {
    return { error: `Invalid type "${type}". Must be one of: domainEvent, decision` }
  }
  type = internalType

  let colorData
  if (color) {
    colorData = getEventColorByName(color)
    if (!colorData) {
      return { error: `Invalid color "${color}". Valid colors: ${EventColorNames.join(', ')}` }
    }
  } else {
    colorData = parentEvent?.color || getEventColorByName('peach')
  }

  const newEventId = generateId()

  let groupId = null
  if (group) {
    const groups = workflow.groups || []
    const matchedGroup = groups.find((g) => g.name === group)
    if (!matchedGroup) {
      return { error: `Group "${group}" not found. Use get_workflow to see available groups.` }
    }
    groupId = matchedGroup.id

    const firstGroup = [...groups].sort((a, b) => a.startSlotX - b.startSlotX)[0]
    if (firstGroup && firstGroup.id === groupId) {
      groupId = null
    }

    // A child placed in the group its parent already anchors would be pinned to the parent's own
    // column. Drop the redundant group so it flows to the next column, like the renderer and the
    // update/connection paths do — the event still sits inside that group's band visually.
    if (groupId && !isStartPoint) {
      const simulatedEvents = [...domainEvents, { id: newEventId, parents: [resolvedParentId], groupId }]
      const reconciled = reconcileEventGroupIds(simulatedEvents)
      if (reconciled.some((update) => update.id === newEventId && update.groupId === null)) {
        groupId = null
      }
    }
  }

  let offset = 0
  if (isStartPoint) {
    // Only start points in the same column can overlap; ones in other groups sit in a different
    // column, so they must not push this one down a row.
    const slotByGroupId = new Map((workflow.groups || []).map((g) => [g.id, g.startSlotX]))
    const newColumn = slotByGroupId.get(groupId) > 0 ? slotByGroupId.get(groupId) : 0
    const usedOffsets = domainEvents
      .filter((e) => {
        if (!e.parents?.includes('start') || e.laneId !== laneId) {
          return false
        }
        const column = slotByGroupId.get(e.groupId) > 0 ? slotByGroupId.get(e.groupId) : 0

        return column === newColumn
      })
      .map((e) => e.offset || 0)
    while (usedOffsets.includes(offset)) {
      offset++
    }
  } else if (isParallel) {
    const siblingOffsets = existingChildren
      .filter((e) => e.laneId === laneId)
      .map((e) => e.offset || 0)
    offset = parentEvent?.laneId === laneId ? (parentEvent?.offset || 0) : 0
    while (siblingOffsets.includes(offset)) {
      offset++
    }
  } else if (parentEvent?.laneId === laneId) {
    offset = parentEvent?.offset || 0
  }

  const reparentedChildren = []
  const eventInputs = []

  if (!isStartPoint && !isParallel && parentEvent?.type === 'bpmn:Task' && existingChildren.length > 0) {
    for (const child of existingChildren) {
      const childIndex = domainEvents.findIndex((e) => e.id === child.id)
      const updatedParents = (child.parents || []).map((p) =>
        p === resolvedParentId ? newEventId : p
      )

      eventInputs.push({
        id: child.id,
        index: childIndex,
        parents: updatedParents,
        expectedVersion: child.version || undefined
      })
      reparentedChildren.push(child.description || child.id)
    }
  }

  const newEventInput = {
    id: newEventId,
    index: domainEvents.length === 0 ? -2 : -1,
    description,
    laneId,
    laneIndex,
    groupId: groupId || null,
    type,
    parents: [resolvedParentId],
    offset,
    color: colorData
  }

  if (conditionLabel && parentEvent?.type === 'bpmn:ExclusiveGateway') {
    newEventInput.conditionLabels = [{ parentId: resolvedParentId, name: conditionLabel }]
  }

  const pendingCards = []
  let runningSortkey = lastCardSortkey

  if (aggregateRoot) {
    const aggregateRootCardType = findCardTypeByRole(cardTypes || [], DomainModelRole.AGGREGATE_ROOT)
    if (!aggregateRootCardType) {
      return { error: 'Aggregate root card type not found in this workflow' }
    }

    const resolvedEntity = resolveSchema(aggregateRoot, workflow)
    if (!resolvedEntity) {
      return { error: `Entity not found for ref: ${aggregateRoot}` }
    }

    const entitySchema = (workflow.schemas || []).find((s) => s.id === resolvedEntity.id)
    if (!entitySchema || entitySchema.type !== 'Entity') {
      return { error: `"${aggregateRoot}" is not an Entity schema` }
    }

    const sortkey = computeMidString(runningSortkey, '')
    runningSortkey = sortkey

    pendingCards.push({
      description: entitySchema.name,
      done: false,
      cardTypeId: aggregateRootCardType.id,
      releaseNo: null,
      sortkey,
      boundedContext: entitySchema.boundedContext || null,
      schemaId: resolvedEntity.id
    })
  }

  if (acceptanceCriteria && acceptanceCriteria.length > 0) {
    const givenWhenThenCardType = findCardTypeByRole(cardTypes || [], DomainModelRole.GIVEN_WHEN_THEN)
    if (!givenWhenThenCardType) {
      return { error: 'Acceptance criteria (Given-When-Then) card type not found in this workflow' }
    }

    for (const text of acceptanceCriteria) {
      const sortkey = computeMidString(runningSortkey, '')
      runningSortkey = sortkey

      pendingCards.push({
        description: text,
        done: false,
        cardTypeId: givenWhenThenCardType.id,
        releaseNo: null,
        sortkey,
        boundedContext: null,
        schemaId: null
      })
    }
  }

  eventInputs.push(newEventInput)

  if (!isStartPoint) {
    eventInputs.push({
      id: resolvedParentId,
      index: parentIndex,
      expectedVersion: parentEvent?.version || undefined
    })
  }

  const groups = workflow.groups || []
  let updatedGroups = null
  if (groups.length > 1) {
    const allEvents = domainEvents.map((e) => {
      const reparentInput = eventInputs.find((input) => input.id === e.id && input.parents)
      if (reparentInput) {
        return { ...e, parents: reparentInput.parents }
      }

      return e
    })
    allEvents.push({
      id: newEventId,
      parents: [resolvedParentId],
      groupId: groupId || null
    })
    updatedGroups = recalculateGroupSlots(allEvents, groups)
  }

  if (settleOffsets) {
    const simulatedEvents = domainEvents.map((existingEvent) => {
      const reparentInput = eventInputs.find((input) => input.id === existingEvent.id && input.parents)

      return reparentInput ? { ...existingEvent, parents: reparentInput.parents } : existingEvent
    })
    simulatedEvents.push({
      id: newEventId,
      laneId,
      groupId: groupId || null,
      type,
      parents: [resolvedParentId],
      offset
    })

    const stableOffsets = computeStableOffsets({
      domainEvents: simulatedEvents,
      lanes: updatedLanes || lanes,
      groups: updatedGroups || groups
    })

    for (const simulatedEvent of simulatedEvents) {
      const newOffset = stableOffsets[simulatedEvent.id]
      const currentOffset = simulatedEvent.id === newEventId ? offset : (simulatedEvent.offset || 0)
      if (newOffset === undefined || newOffset === currentOffset) {
        continue
      }
      if (simulatedEvent.id === newEventId) {
        offset = newOffset
        newEventInput.offset = newOffset

        continue
      }
      const existingInput = eventInputs.find((input) => input.id === simulatedEvent.id)
      if (existingInput) {
        existingInput.offset = newOffset
      } else {
        const existingIndex = domainEvents.findIndex((e) => e.id === simulatedEvent.id)
        eventInputs.push({
          id: simulatedEvent.id,
          index: existingIndex,
          offset: newOffset,
          expectedVersion: domainEvents[existingIndex]?.version || undefined
        })
      }
    }
  }

  const mutationInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    domainEvents: appendMissingParentInputs(eventInputs, domainEvents),
    updateNote: {
      action: 'added event',
      target: description
    }
  }

  if (updatedGroups) {
    mutationInput.groups = updatedGroups
  }

  if (updatedLanes) {
    mutationInput.lanes = updatedLanes
    mutationInput.expectedLanesVersion = workflow.lanesVersion
  }

  const typeName = type === 'bpmn:Task' ? 'Event' : 'Decision'
  let summary = `${typeName} "${description}" created successfully`
  if (laneAutoCreated) {
    summary += `. Lane "${trimmedLane}" was auto-created.`
  }
  if (reparentedChildren.length > 0) {
    summary += `. Reparented ${reparentedChildren.length} child event(s): ${reparentedChildren.join(', ')}`
  }
  if (isParallel && existingChildren.length > 0) {
    summary += `. Added as a parallel branch alongside ${existingChildren.length} existing event(s)`
  }

  return {
    mutationInput,
    newEventId,
    description,
    type,
    laneId,
    groupId: groupId || null,
    resolvedParentId,
    offset,
    colorData,
    eventInputs,
    pendingCards,
    updatedLanes,
    updatedGroups,
    lastCardSortkey: runningSortkey,
    laneAutoCreated,
    trimmedLane,
    reparentedChildren,
    existingChildrenCount: existingChildren.length,
    isParallel,
    domainEventsCount: domainEvents.length,
    summary
  }
}
